import {gql} from '@apollo/client';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useMemo} from 'react';

import {ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import ResultsPageLoader from 'nav/sidepanel/listing/ResultsPageLoader';
import SidepanelItem from 'nav/sidepanel/listing/SidepanelItem';
import {RealtimeMessageType} from 'realtime/Constants';
import RealtimeUpdateSubscriptionWrapper from 'realtime/RealtimeUpdateSubscriptionWrapper';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import useFuzzyDate from 'ui-library/hooks/useFuzzyDate';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {Container} from 'ui-library/NYC';
import {readableTime} from 'util/DateUtil';
import {assertNever} from 'util/Types';

import {ChatHistoryQuery, ChatHistoryQueryVariables} from './__generated/ChatHistoryQuery';
import {getChatNameRenderInfo} from './ChatNameUtil';
import type {ChatRequest} from './ChatRouter';

interface ChatSidepanelItem {
  id: string | null;
  type: ChatType;
  name: string | null;
  modifiedAt: string;
}

interface ChatHistoryFetchState {
  prevTokens: Array<string>;
  currentToken: string;
  nextToken: string | null;
  items: Array<ChatHistoryQuery['chatSessionListing']['items'][number]>;
  fetching: boolean;
}

const INITIAL_CHAT_HISTORY_FETCH_STATE: ChatHistoryFetchState = {
  prevTokens: [],
  currentToken: '',
  nextToken: null,
  items: [],
  fetching: false,
};

const chatHistoryFetchStateHooks = makeUseGlobalClientState<ChatHistoryFetchState>(
  INITIAL_CHAT_HISTORY_FETCH_STATE
);

export const CHAT_HISTORY_QUERY = gql`
  query ChatHistoryQuery($nextToken: StrictString) @engComponent(name: LLM) @skipAudit {
    chatSessionListing(pageSize: 15, nextToken: $nextToken) {
      items {
        id
        type
        name
        modifiedAt
      }
      total
      nextToken
    }
  }
`;

function Timestamp(props: {timestamp: string}): JSX.Element {
  const formattedTime = useMemo(() => readableTime(props.timestamp), [props.timestamp]);
  return <>{formattedTime}</>;
}

function ChatHistoryItem(props: {
  className?: string;
  item: ChatSidepanelItem;
  onSelect: (chat: ChatRequest) => void;
  isSelected: boolean;
}): JSX.Element {
  const fuzzyDate = useFuzzyDate(props.item.modifiedAt);
  return (
    <SidepanelItem
      className={props.className}
      dataTestAttrs={{
        component: 'ChatHistoryItem',
        element: 'root',
        key: props.item.name ?? '',
        ...(props.isSelected ? {index: 'active'} : {}),
      }}
      icon={<BenchlingIcon icon="chat" />}
      isCompact={false}
      title={getChatNameRenderInfo(props.item)}
      subTitle={
        <>
          <span>Last modified </span>
          <Tooltip text={<Timestamp timestamp={props.item.modifiedAt} />}>
            <>{fuzzyDate}</>
          </Tooltip>
        </>
      }
      onClick={() => {
        props.onSelect({id: props.item.id, type: props.item.type});
      }}
      isExpandable={false}
      isChild={false}
      active={props.isSelected}
    />
  );
}

interface ChatListingProps {
  fetchNext: (() => void) | null;
  fetchPrevious: (() => void) | null;
  fetchCurrent: () => void;
  onSelectChatSession: (chat: ChatRequest) => void;
  selectedChatSession: ChatRequest | null;
}

function PresentationalChatListing(props: ChatListingProps & {items: Array<ChatSidepanelItem>}): JSX.Element {
  const placeholderItems =
    props.selectedChatSession && props.selectedChatSession.id == null && props.fetchPrevious == null
      ? [{id: null, name: null, type: props.selectedChatSession.type, modifiedAt: new Date().toISOString()}]
      : [];
  const allItems = [...placeholderItems, ...props.items];
  if (allItems.length === 0) {
    return (
      <div className={css(styles.emptyChatListing)}>
        <div className={css(styles.emptyChatListingMessage)}>
          <p className={css(styles.emptyChatListingMessageTitle)}>No chats found</p>
          <p>
            Start a new chat with the <BenchlingIcon icon="plus" /> icon above.
          </p>
        </div>
      </div>
    );
  }
  return (
    <Container size="sidepanel" dataTestAttrs={{component: 'ChatListing', element: 'root'}}>
      {props.fetchPrevious && (
        <ResultsPageLoader isPrevious={true} limit={2} setOffset={props.fetchPrevious} offset={0} />
      )}
      {allItems.map((item, idx) => {
        if (item.id == null) {
          return (
            <ChatHistoryItem
              className={idx === 0 ? css(styles.firstChatHistoryItem) : undefined}
              key="new-chat"
              item={item}
              onSelect={props.onSelectChatSession}
              isSelected={props.selectedChatSession?.id === item.id}
            />
          );
        }
        return (
          <RealtimeUpdateSubscriptionWrapper
            apiId={item.id}
            key={item.id}
            messageFilter={[RealtimeMessageType.CHAT_NAME_UPDATED]}
            refetch={props.fetchCurrent}
          >
            <ChatHistoryItem
              className={idx === 0 ? css(styles.firstChatHistoryItem) : undefined}
              item={item}
              onSelect={props.onSelectChatSession}
              isSelected={props.selectedChatSession?.id === item.id}
            />
          </RealtimeUpdateSubscriptionWrapper>
        );
      })}
      {props.fetchNext && (
        <ResultsPageLoader isPrevious={false} limit={2} setOffset={props.fetchNext} offset={0} />
      )}
    </Container>
  );
}

export default function ChatListing({
  onSelectChatSession,
  selectedChatSession,
  refetchRef,
}: {
  onSelectChatSession: (chat: ChatRequest) => void;
  selectedChatSession: ChatRequest | null;
  refetchRef: React.MutableRefObject<() => void>;
}): JSX.Element {
  const [fetchState, setFetchState] = chatHistoryFetchStateHooks.useGlobalClientState(
    'ai-sidepanel-chat-history-list'
  );

  const result = useBenchlingQuery<ChatHistoryQuery, ChatHistoryQueryVariables>(CHAT_HISTORY_QUERY, {
    variables: {nextToken: fetchState.currentToken},
  });

  useEffect(() => {
    refetchRef.current = () => {
      setFetchState(INITIAL_CHAT_HISTORY_FETCH_STATE);
      result.refetch();
    };
  }, [setFetchState, refetchRef, result]);

  useEffect(() => {
    if (result.type === QueryResultType.SUCCESS) {
      // We keep track of just the item ids, to encourage the item to read directly from the cache,
      setFetchState({
        prevTokens: fetchState.prevTokens,
        currentToken: fetchState.currentToken,
        nextToken: result.data.chatSessionListing.nextToken,
        items: result.data.chatSessionListing.items,
        fetching: false,
      });
    }
  }, [result, setFetchState, fetchState.currentToken, fetchState.prevTokens]);

  const fetchNext = useMemo(() => {
    if (result.type === QueryResultType.SUCCESS && result.data.chatSessionListing.nextToken) {
      return () => {
        setFetchState({
          prevTokens: [...fetchState.prevTokens, fetchState.currentToken],
          currentToken: result.data.chatSessionListing.nextToken,
          nextToken: null,
          items: fetchState.items,
          fetching: true,
        });
      };
    } else {
      return null;
    }
  }, [result, setFetchState, fetchState]);

  const fetchPrevious = useMemo(() => {
    if (fetchState.prevTokens.length > 0) {
      return () => {
        setFetchState({
          prevTokens: fetchState.prevTokens.slice(0, -1),
          currentToken: fetchState.prevTokens[fetchState.prevTokens.length - 1],
          nextToken: null,
          items: fetchState.items,
          fetching: false,
        });
      };
    } else {
      return null;
    }
  }, [fetchState, setFetchState]);

  const sharedListProps = {
    fetchNext,
    fetchPrevious,
    fetchCurrent: result.refetch,
    onSelectChatSession,
    selectedChatSession,
  } satisfies ChatListingProps;
  switch (result.type) {
    case QueryResultType.SUCCESS:
      return <PresentationalChatListing {...sharedListProps} items={result.data.chatSessionListing.items} />;
    case QueryResultType.ERROR:
      return (
        <Container size="sidepanel" className={css(styles.errorContainer)}>
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            text="Error loading chats."
            action={{
              actionType: InlineBannerActionType.CUSTOM,
              actionText: 'Retry',
              actionOnClick: () => {
                result.refetch();
              },
              rightAlign: true,
            }}
          />
        </Container>
      );
    case QueryResultType.PENDING:
      return (
        <div style={{opacity: 0.5}}>
          {fetchState.items.length ? (
            <PresentationalChatListing {...sharedListProps} items={fetchState.items} />
          ) : (
            <CenteredHorizontalThrobber />
          )}
        </div>
      );
    default:
      throw new Error(`Unknown result type: ${assertNever(result).type}`);
  }
}

const styles = StyleSheet.create({
  firstChatHistoryItem: {
    borderTop: `1px solid transparent`,
  },
  emptyChatListing: {
    flex: 0.3,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingTop: '20px',
  },
  emptyChatListingMessageTitle: {
    color: Color.GRAY_600,
    fontSize: '18px',
  },
  emptyChatListingMessage: {
    color: Color.GRAY_500,
    fontSize: '14px',
    lineHeight: '20px',
  },
  errorContainer: {
    padding: '12px',
  },
});
